import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { ChevronRight, ArrowLeft, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  civilite: '', prenom: '', nom: '', deuxieme_nom: '', email: '', code_interne: '',
  nationalite: '', nom_naissance: '', ville_naissance: '', departement_naissance: '',
  date_naissance: '', langue: 'Français', numero_securite_sociale: '',
  adresse: '', code_postal: '', ville: '', pays: 'France FR', telephone: '',
  statut_intervenant: 'Interne', modele_tarifaire: '', specialty: '', bio: '', disponibilite: 'Disponible',
};

const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <label className="block text-xs text-slate-500 mb-1">{label}</label>
    {children}
  </div>
);

const SectionTitle = ({ children }) => (
  <h2 className="text-lg font-bold text-slate-900 mt-8 mb-5 first:mt-0">{children}</h2>
);

const inputCls = "w-full border-0 border-b border-slate-300 rounded-none bg-slate-50/50 px-3 py-2.5 text-sm focus:ring-0 focus:border-violet-500 outline-none transition-colors";
const selectCls = "w-full border-0 border-b border-slate-300 rounded-none bg-slate-50/50 px-3 py-2.5 text-sm focus:ring-0 focus:border-violet-500 outline-none appearance-none";

const IntervenantDetailPage = () => {
  const { intervenantId } = useParams();
  const navigate = useNavigate();
  const isNew = !intervenantId || intervenantId === 'nouveau';
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });

  const fetchIntervenant = useCallback(async () => {
    if (isNew) return;
    try {
      const res = await fetch(`${API}/donnees-intervenants/${intervenantId}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setForm(prev => ({ ...prev, ...data }));
      } else {
        toast.error("Intervenant non trouvé");
        navigate('/donnees/intervenants');
      }
    } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  }, [intervenantId, isNew, navigate]);

  useEffect(() => { fetchIntervenant(); }, [fetchIntervenant]);

  const handleSave = async () => {
    if (!form.nom?.trim()) { toast.error("Le nom est obligatoire"); return; }
    setSaving(true);
    try {
      const url = isNew ? `${API}/donnees-intervenants` : `${API}/donnees-intervenants/${intervenantId}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form), credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(isNew ? 'Intervenant créé' : 'Intervenant mis à jour');
        if (isNew && data.intervenant_id) navigate(`/donnees/intervenants/${data.intervenant_id}`, { replace: true });
      } else { toast.error("Erreur lors de l'enregistrement"); }
    } catch { toast.error("Erreur réseau"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Supprimer cet intervenant ?")) return;
    await fetch(`${API}/donnees-intervenants/${intervenantId}`, { method: 'DELETE', credentials: 'include' });
    toast.success("Intervenant supprimé");
    navigate('/donnees/intervenants');
  };

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  if (loading) {
    return (
      <Layout>
        <div className="space-y-4">
          <div className="h-8 w-48 bg-slate-100 rounded animate-pulse" />
          <div className="h-96 bg-slate-100 rounded-xl animate-pulse" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-5" data-testid="intervenant-detail-page">
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <span>Données</span>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="cursor-pointer hover:text-violet-500 transition-colors" onClick={() => navigate('/donnees/intervenants')}>Intervenants</span>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-slate-600 font-medium">{isNew ? 'Nouvel intervenant' : `${form.prenom} ${form.nom}`}</span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/donnees/intervenants')} className="w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors" data-testid="back-btn">
              <ArrowLeft className="h-5 w-5 text-slate-600" />
            </button>
            <h1 className="text-2xl font-black text-slate-900" data-testid="page-title">
              {isNew ? 'Nouvel intervenant' : `${form.prenom} ${form.nom}`}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {!isNew && (
              <Button variant="outline" size="sm" onClick={handleDelete} className="text-red-500 border-red-200 hover:bg-red-50" data-testid="delete-btn">
                <Trash2 className="h-4 w-4 mr-1.5" /> Supprimer
              </Button>
            )}
          </div>
        </div>

        {/* Form */}
        <div className="bg-white border border-slate-200 rounded-2xl p-8">
          <SectionTitle>Informations générales</SectionTitle>
          <div className="grid grid-cols-2 gap-x-8 gap-y-5">
            <div className="col-span-2 grid grid-cols-4 gap-x-8">
              <Field label="Civilité">
                <select className={selectCls} value={form.civilite} onChange={e => set('civilite', e.target.value)} data-testid="input-civilite">
                  <option value="">—</option>
                  <option value="M">M</option>
                  <option value="Mme">Mme</option>
                </select>
              </Field>
              <Field label="Prénom" className="col-span-3">
                <input className={inputCls} value={form.prenom} onChange={e => set('prenom', e.target.value)} data-testid="input-prenom" />
              </Field>
            </div>
            <Field label="Nom *">
              <input className={inputCls} value={form.nom} onChange={e => set('nom', e.target.value)} data-testid="input-nom" />
            </Field>
            <Field label="Email">
              <input className={inputCls} type="email" value={form.email} onChange={e => set('email', e.target.value)} data-testid="input-email" />
            </Field>
            <Field label="Code interne">
              <input className={inputCls} value={form.code_interne} onChange={e => set('code_interne', e.target.value)} data-testid="input-code-interne" />
            </Field>
            <Field label="Nationalité">
              <input className={inputCls} value={form.nationalite} onChange={e => set('nationalite', e.target.value)} data-testid="input-nationalite" />
            </Field>
            <Field label="Nom de naissance">
              <input className={inputCls} value={form.nom_naissance} onChange={e => set('nom_naissance', e.target.value)} data-testid="input-nom-naissance" />
            </Field>
            <Field label="Ville de naissance">
              <input className={inputCls} value={form.ville_naissance} onChange={e => set('ville_naissance', e.target.value)} data-testid="input-ville-naissance" />
            </Field>
            <Field label="Département de naissance">
              <input className={inputCls} value={form.departement_naissance} onChange={e => set('departement_naissance', e.target.value)} data-testid="input-dept-naissance" />
            </Field>
            <Field label="Date de naissance">
              <input className={inputCls} type="date" value={form.date_naissance} onChange={e => set('date_naissance', e.target.value)} data-testid="input-date-naissance" />
            </Field>
            <Field label="Langue">
              <select className={selectCls} value={form.langue} onChange={e => set('langue', e.target.value)} data-testid="input-langue">
                <option value="Français">Français</option>
                <option value="English">English</option>
                <option value="Español">Español</option>
              </select>
            </Field>
            <Field label="N° sécurité sociale">
              <input className={inputCls} value={form.numero_securite_sociale} onChange={e => set('numero_securite_sociale', e.target.value)} data-testid="input-secu" />
            </Field>
            <Field label="Adresse">
              <input className={inputCls} value={form.adresse} onChange={e => set('adresse', e.target.value)} data-testid="input-adresse" />
            </Field>
            <Field label="Code postal">
              <input className={inputCls} value={form.code_postal} onChange={e => set('code_postal', e.target.value)} data-testid="input-code-postal" />
            </Field>
            <Field label="Ville">
              <input className={inputCls} value={form.ville} onChange={e => set('ville', e.target.value)} data-testid="input-ville" />
            </Field>
            <Field label="Pays">
              <select className={selectCls} value={form.pays} onChange={e => set('pays', e.target.value)} data-testid="input-pays">
                <option value="France FR">France FR</option>
                <option value="CM">Cameroun CM</option>
                <option value="BE">Belgique BE</option>
                <option value="CH">Suisse CH</option>
              </select>
            </Field>
            <Field label="Téléphone">
              <input className={inputCls} value={form.telephone} onChange={e => set('telephone', e.target.value)} data-testid="input-telephone" />
            </Field>
          </div>
        </div>

        {/* Statut & Tarification */}
        <div className="bg-violet-50/50 border border-violet-100 rounded-2xl p-8">
          <SectionTitle>Statut & Tarification</SectionTitle>
          <div className="grid grid-cols-2 gap-x-8 gap-y-5">
            <Field label="Statut de l'intervenant">
              <select className={selectCls} value={form.statut_intervenant} onChange={e => set('statut_intervenant', e.target.value)} data-testid="input-statut">
                <option value="Interne">Interne</option>
                <option value="Externe">Externe</option>
              </select>
            </Field>
            <Field label="Modèle tarifaire">
              <select className={selectCls} value={form.modele_tarifaire} onChange={e => set('modele_tarifaire', e.target.value)} data-testid="input-modele-tarifaire">
                <option value="">—</option>
                <option value="Au forfait">Au forfait</option>
                <option value="Par heure">Par heure</option>
                <option value="Par jour">Par jour</option>
              </select>
            </Field>
            <Field label="Disponibilité">
              <select className={selectCls} value={form.disponibilite} onChange={e => set('disponibilite', e.target.value)} data-testid="input-disponibilite">
                <option value="Disponible">Disponible</option>
                <option value="Occupé">Occupé</option>
                <option value="En congé">En congé</option>
              </select>
            </Field>
          </div>
        </div>

        {/* Champs personnalisés */}
        <div className="bg-slate-50/80 border border-slate-200 rounded-2xl p-8">
          <SectionTitle>Champs personnalisés</SectionTitle>
          <div className="grid grid-cols-2 gap-x-8 gap-y-5">
            <Field label="Spécialité">
              <input className={inputCls} value={form.specialty} onChange={e => set('specialty', e.target.value)} data-testid="input-specialty" />
            </Field>
            <Field label="Deuxième nom de famille">
              <input className={inputCls} value={form.deuxieme_nom} onChange={e => set('deuxieme_nom', e.target.value)} data-testid="input-deuxieme-nom" />
            </Field>
            <Field label="Bio / Description" className="col-span-2">
              <textarea className="w-full border-0 border-b border-slate-300 rounded-none bg-slate-50/50 px-3 py-2.5 text-sm focus:ring-0 focus:border-violet-500 outline-none min-h-[80px]" value={form.bio} onChange={e => set('bio', e.target.value)} data-testid="input-bio" />
            </Field>
          </div>
        </div>

        <Button onClick={handleSave} disabled={saving} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="save-btn">
          <Save className="h-4 w-4 mr-1.5" /> {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>
    </Layout>
  );
};

export default IntervenantDetailPage;
